import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

// `title` is the panel's own, not the HTML tooltip attribute: a refusal's
// title is a line of text on the page, never a hover.
export interface EmptyStateProps extends Omit<
  React.ComponentProps<'div'>,
  'title'
> {
  /** Single-colour line art. Never an illustration — see docs/brand.md FD-15. */
  icon?: LucideIcon;
  /**
   * A composed mark from `emptyMarks.tsx` (spec 122 FR-022), for the six
   * empty states that are a destination rather than a failed read. Wins over
   * `icon` when both are given; the failed-read branches keep the icon.
   */
  mark?: ReactNode;
  /**
   * One line that names the state before the description explains it (spec
   * 122 FR-020): "Administrators only", then why. A refusal without a title
   * read as a paragraph of apology with no subject.
   */
  title?: ReactNode;
  /** Why the region is empty, or what went wrong, in plain language. */
  description: ReactNode;
  /** The way on: retry, or the action that would fill the region. */
  action?: ReactNode;
  className?: string;
}

/**
 * The one panel every "there is nothing here" and "this could not be loaded"
 * region uses: dashed border, line-art icon, prose, and a way on.
 *
 * It existed as copied markup in five places before this — `PagePlaceholder`'s
 * body, three branches of the workflow list, and every state in the file
 * browser except the ones that quietly dropped the border and the icon. Those
 * regions read as unfinished rather than as empty, which is the opposite of
 * what an empty state is for.
 */
export function EmptyState({
  icon: Icon,
  mark,
  title,
  description,
  action,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'border-border flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-10 text-center',
        className,
      )}
      {...props}
    >
      {mark ??
        (Icon ? (
          <Icon
            aria-hidden="true"
            className="text-muted-foreground size-6"
            strokeWidth={1.5}
          />
        ) : null)}
      {title ? (
        <p className="text-foreground text-sm font-medium">{title}</p>
      ) : null}
      <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
        {description}
      </p>
      {action}
    </div>
  );
}
